import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { now, tzDate } from "shared/utils/dayjs";

const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

interface UseDisabledStartTimeParams {
  scheduleDate: Dayjs | undefined;
  // Create-panel backfills: a class that already ran earlier today may be
  // recorded, so today's past times stay selectable. The reschedule modal
  // leaves this off and keeps the filter.
  allowPastTimes?: boolean;
}

interface DisabledStartTimes {
  disabledHours: number[];
  disabledMinutesByHour: Record<number, number[]>;
}

/**
 * Disables only PAST start times (on today's date). Clash-based disabling was
 * removed — clashing times are now selectable and surfaced through the
 * warning-before-create/reschedule flow instead of being greyed out here.
 */
export const useDisabledStartTime = ({
  scheduleDate,
  allowPastTimes = false,
}: UseDisabledStartTimeParams) => {
  const earliestStart = useMemo(() => {
    if (allowPastTimes) return undefined;
    // scheduleDate is an AntD-sourced Dayjs (no utc/timezone plugins); normalize
    // onto our instance so it never mixes with now() in a comparison chain.
    const normalized = scheduleDate ? tzDate(scheduleDate) : undefined;
    const isToday = normalized?.isSame(now(), "day") ?? false;
    return isToday ? now() : undefined;
  }, [scheduleDate, allowPastTimes]);

  const disabledStartTimes = useMemo<DisabledStartTimes>(() => {
    const disabledHours: number[] = [];
    const disabledMinutesByHour: Record<number, number[]> = {};

    // Only today needs filtering; any other (future) date has no past times.
    if (!scheduleDate || !earliestStart) {
      return { disabledHours, disabledMinutesByHour };
    }
    const dayStart = tzDate(scheduleDate).startOf("day");

    for (let h = 0; h < HOURS_IN_DAY; h++) {
      const disabledMinutes: number[] = [];
      for (let m = 0; m < MINUTES_IN_HOUR; m++) {
        if (dayStart.hour(h).minute(m).isBefore(earliestStart)) {
          disabledMinutes.push(m);
        }
      }
      disabledMinutesByHour[h] = disabledMinutes;

      if (dayStart.hour(h).minute(59).isBefore(earliestStart)) {
        disabledHours.push(h);
      }
    }

    return { disabledHours, disabledMinutesByHour };
  }, [earliestStart, scheduleDate]);

  return useMemo(
    () => () => ({
      disabledHours: () => disabledStartTimes.disabledHours,
      disabledMinutes: (hour: number) =>
        disabledStartTimes.disabledMinutesByHour[hour] ?? [],
    }),
    [disabledStartTimes],
  );
};
